import {
  useEnableCollaborationMutation,
  useGetOrgInfoQuery,
} from "@api/accounts";
import { useAppSelector } from "@store";
import { getAccountSeats } from "@store/account/selectors";
import { useEffect, useRef } from "react";

/**
 * Latched per caller: the first answer given once the org-info query has
 * settled is the answer for that component's whole lifetime.
 *
 * Callers gate on this by swapping which component sits at a position
 * (`CollaborationEnabledProvider fallback={children}` in Room, CurrentSession,
 * UserPresence, BlockFeatureCollaboration). React unmounts a subtree when the
 * type at a position changes, so a flip after mount tears the editor down:
 * RoomProvider re-joins the room (another authorize + hocuspocus-token),
 * YjsStoryProvider re-runs its initial seed, and use-get-story's ref guards are
 * wiped. Both directions are reachable — org info going undefined on a 401 or
 * cache eviction (true -> false), or `collaborationEnabled` turning on
 * server-side for a single-seat org (false -> true).
 *
 * Freezing only after the query settles is what makes this safe: the loading
 * render is deliberately not latched, or every caller would be stuck on the
 * `planLimit = 1` default and collaboration would never turn on. A genuine
 * change still takes effect — on the next mount, or a reload.
 */
export const useCollaborationStatus = () => {
  const { data: account, isLoading } = useGetOrgInfoQuery({});
  const hasMoreThanOneSeat = useAppSelector((s) => {
    const { planLimit, additionalSeats, freeSeats } = getAccountSeats(s);
    return planLimit + additionalSeats + freeSeats > 1;
  });
  const [enableCollaboration] = useEnableCollaborationMutation();

  useEffect(() => {
    if (!account?.collaborationEnabled && hasMoreThanOneSeat) {
      enableCollaboration({});
    }
  }, [account?.collaborationEnabled, hasMoreThanOneSeat, enableCollaboration]);

  const isEnabled = !!account?.collaborationEnabled || hasMoreThanOneSeat;

  // Written during render on purpose: the latch has to hold for the render that
  // establishes it, not one commit later, or the first gated render still uses
  // the unlatched value. Assigning the same value twice is harmless.
  const latched = useRef<boolean | null>(null);
  if (latched.current === null && !isLoading) latched.current = isEnabled;

  return latched.current ?? isEnabled;
};
